<script setup lang="ts">
import { onUnmounted } from "vue";
import {
  useVisible$,
  Timeline,
  TWEEN,
  DrawContext,
  type InterpolationFunction
} from "@zyzgroup/core-web";
import Button from "../form/Button.vue";
import Box from "../base/Box.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { Button, Box },
  inheritAttrs: true
});

const props = withDefaults(
  defineProps<{
    interpolationName: string;
    interpolationFunction: InterpolationFunction;
    canvasWidth?: number;
    canvasHeight?: number;
    interpolationDuration?: number;
  }>(),
  {
    canvasWidth: 240,
    canvasHeight: 160,
    interpolationDuration: 2000
  }
);

const emits = defineEmits<{
  (event: "mount", easeName: string): void;
  (event: "start", easeName: string): void;
  (event: "complete", easeName: string): void;
}>();

let drawContext: DrawContext;
let subscription: any;
let isRunning = false;

const start = (ctx, box) => {
  if (subscription) {
    return;
  }
  drawContext = ctx;
  emits("mount", props.interpolationName);
  subscription = useVisible$(box.el).subscribe((v) => {
    if (!v) {
      return;
    }
    if (isRunning) {
      return;
    }
    forceStart();
  });
};

const min = 1 / 6;
const max = 5 / 6;

const x0 = props.canvasWidth * min;
const y0 = props.canvasHeight / 2;

const xA = [props.canvasWidth * max, props.canvasWidth / 2];
const yA = [props.canvasHeight * min, props.canvasHeight * max];

/// start === end
const xB = [...xA, x0];
const yB = [...yA, y0];

const forceStart = () => {
  isRunning = true;

  emits("start", props.interpolationName);

  // 背景颜色
  drawContext.ctx.fillStyle = "rgb(250,250,250)";
  drawContext.ctx.fillRect(0, 0, props.canvasWidth, props.canvasHeight);

  drawContext.ctx.fillStyle = "rgb(200,200,200)";
  drawContext.ctx.fillRect(x0 - 3, y0 - 3, 6, 6);
  drawContext.ctx.fillRect(xA[xA.length - 1] - 3, yA[yA.length - 1] - 3, 6, 6);

  for (let i = 0; i < xA.length; i++) {
    drawContext.ctx.fillRect(xA[i] - 2, yA[i] - 2, 4, 4);
  }

  const tl = new Timeline({ frameDuration: props.interpolationDuration });

  tl.addTween(
    { x: x0, y: y0, old: { x: x0, y: y0 } },
    { x: xA, y: yA },
    {
      frames: 1,
      delay: 0,
      easeFunction: TWEEN.Easing.Linear.None,
      interpolationFunction: props.interpolationFunction,
      onUpdate: (object) => {
        drawContext.ctx.lineWidth = 2;
        drawContext.ctx.strokeStyle = "rgba(255,127,127,0.9)";

        drawContext.ctx.beginPath();
        drawContext.ctx.moveTo(object.old.x, object.old.y);
        drawContext.ctx.lineTo(object.x, object.y);
        drawContext.ctx.closePath();
        drawContext.ctx.stroke();

        object.old.x = object.x;
        object.old.y = object.y;
      },
      onComplete: () => {}
    }
  );

  tl.addTween(
    { x: x0, y: y0, old: { x: x0, y: y0 } },
    { x: xB, y: yB },
    {
      frames: 1,
      delay: 0,
      easeFunction: TWEEN.Easing.Linear.None,
      interpolationFunction: props.interpolationFunction,
      onUpdate: (object) => {
        drawContext.ctx.lineWidth = 2;
        drawContext.ctx.strokeStyle = "rgba(255,127,127,0.5)";

        drawContext.ctx.beginPath();
        drawContext.ctx.moveTo(object.old.x, object.old.y);
        drawContext.ctx.lineTo(object.x, object.y);
        drawContext.ctx.closePath();
        drawContext.ctx.stroke();

        object.old.x = object.x;
        object.old.y = object.y;
      },
      onComplete: () => {
        emits("complete", props.interpolationName);
        isRunning = false;
      }
    }
  );
  tl.play();
};

onUnmounted(() => {
  subscription?.unsubscribe();
});

defineExpose({ start: forceStart });
</script>

<template>
  <div class="interpolation-graph-display">
    <div>
      插值：{{ interpolationName }}
      <Button
        stylePattern="outline"
        height="32px"
        @click="forceStart"
      >
        开始
      </Button>
    </div>
    <div
      :style="{
        width: canvasWidth + 'px',
        height: canvasHeight + 'px',
        backgroundColor: '#ffffff'
      }"
    >
      <Box
        type="canvas"
        @start="start"
      ></Box>
    </div>
  </div>
</template>

<!--
  ce 中不要使用v-bind
  color: v-bind(fontColor);
-->
<style scoped>
.interpolation-graph-display {
  display: inline-block;
}
.interpolation-graph-display > div:nth-child(1) {
  margin-top: 10px;
  margin-bottom: 10px;
  font-size: 14px;
}
</style>
